// Onboarding step 2b (create path, docs/06 §2: "confirm!"): quiz 3 random
// words from the 24-word phrase to prove the user actually wrote it down.

import { useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { useT } from '../../i18n/index.js';

/** count unique random 1-based positions in [1, total]. */
function pickPositions(count: number, total: number): number[] {
  const positions = new Set<number>();
  while (positions.size < count) {
    positions.add(1 + Math.floor(Math.random() * total));
  }
  return [...positions].sort((a, b) => a - b);
}

export function ConfirmPhrase({
  words,
  onConfirmed,
  onBack,
}: {
  words: string[];
  onConfirmed: () => void;
  onBack: () => void;
}): ReactNode {
  const t = useT();
  const positions = useMemo(() => pickPositions(3, words.length), [words]);
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [error, setError] = useState<string | undefined>(undefined);

  function handleSubmit(e: FormEvent): void {
    e.preventDefault();
    const allCorrect = positions.every((pos) => {
      const expected = (words[pos - 1] ?? '').trim().toLowerCase();
      const actual = (answers[pos] ?? '').trim().toLowerCase();
      return expected.length > 0 && expected === actual;
    });
    if (!allCorrect) {
      setError(t('confirmPhrase.error'));
      return;
    }
    setError(undefined);
    onConfirmed();
  }

  return (
    <main className="mx-auto flex min-h-full w-full max-w-app flex-col gap-6 p-6">
      <h1 className="text-2xl font-semibold text-ink">{t('confirmPhrase.title')}</h1>
      <p className="text-sm text-muted">{t('confirmPhrase.help')}</p>
      <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
        {positions.map((pos) => (
          <label key={pos} className="flex flex-col gap-1 text-sm text-ink">
            {t('confirmPhrase.wordLabel', { n: pos })}
            <input
              type="text"
              value={answers[pos] ?? ''}
              onChange={(e) => setAnswers((prev) => ({ ...prev, [pos]: e.target.value }))}
              autoComplete="off"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              className="min-h-touch rounded-xl border border-edge bg-surface px-3 text-ink"
            />
          </label>
        ))}
        {error !== undefined && (
          <p role="alert" className="text-sm text-danger">
            {error}
          </p>
        )}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onBack}
            className="min-h-touch flex-1 rounded-xl border border-edge bg-surface px-4 font-medium text-ink"
          >
            {t('common.back')}
          </button>
          <button
            type="submit"
            className="min-h-touch flex-1 rounded-xl bg-accent px-4 font-medium text-ink active:bg-accent-press"
          >
            {t('common.continue')}
          </button>
        </div>
      </form>
    </main>
  );
}
